import React, { useState } from 'react';

const roles = [
  {
    company: 'Apex Systems',
    client: '(Client: Enterprise Fleets)',
    title: 'Software Engineer in Test',
    dates: 'May 2024 — Present',
    bullets: [
      'Design and build Karate API test suites against Java/Spring Boot and Quarkus microservices on a fleet-management platform — positive flows, negative and boundary cases, auth, and error-contract assertions.',
      'Configure and maintain Jenkins pipelines to run the suites across multiple environments, with automated failure reporting back to the squad.',
      'Review QA pull requests across the squad — checking test design, endpoint coverage, and adherence to framework conventions.',
      'Read service code daily (controllers, exception mappers, configuration) and take on backend tickets in the same Quarkus/Spring Boot services — bug fixes, OpenAPI contract changes, reporting features — so test design matches how the code actually behaves.',
      'Mentor QA engineers on Java and Karate testing patterns, and maintain shared test-utility libraries adopted across squads.',
    ],
    tech: ['Java', 'Spring Boot', 'Quarkus', 'Karate', 'Jenkins', 'AWS', 'PostgreSQL'],
  },
  {
    company: 'Clover Network / Fiserv',
    title: 'Senior Software Test Engineer',
    dates: 'Dec 2020 — Apr 2024',
    bullets: [
      'Stood up multiple standalone Karate projects for microservices, giving each team an isolated, reproducible API test environment.',
      'Built Pact contract tests across producer/consumer services and integrated a Pact broker into the CI/CD pipeline to gate deploys on contract compatibility.',
      'Authored Cypress and Playwright UI tests against React/TypeScript front-ends, covering core user flows.',
      'Configured nightly Jenkins jobs across dev and staging environments, with automated failure notifications to Slack and email.',
      'Mentored SDETs across onshore and offshore teams, sharing framework patterns and review feedback.',
    ],
    tech: ['Karate', 'Pact', 'Cypress', 'Playwright', 'React', 'TypeScript', 'Jenkins'],
  },
  {
    company: 'Sallie Mae',
    title: 'Software Development Engineer in Test',
    dates: 'Sep 2017 — Nov 2020',
    bullets: [
      'Built a BDD SpecFlow framework from scratch for front-end applications in the C#/.NET tech stack.',
      'Performed API testing with RestSharp; used BrowserStack for cross-browser coverage.',
      'Leveraged Azure DevOps for pipeline configuration, test execution, and bug tracking.',
      'Migrated manual test cases to automated scripts, cutting test cycle times materially.',
    ],
    tech: ['C#', '.NET', 'SpecFlow', 'RestSharp', 'BrowserStack', 'Azure DevOps'],
  },
];

const Experience = () => {
  const [active, setActive] = useState(0);
  const role = roles[active];

  return (
    <section name='experience' className='w-full py-24 sm:py-32 bg-bg'>
      <div className='container-narrow'>
        <p className='section-eyebrow'>02. Where I've worked</p>
        <h2 className='section-title'>Experience</h2>
        <div className='w-12 h-1 bg-accent rounded-full mb-12' />

        <div className='grid lg:grid-cols-[220px_1fr] gap-8 lg:gap-12'>
          {/* Company tabs */}
          <div className='flex lg:flex-col overflow-x-auto lg:overflow-visible border-b lg:border-b-0 lg:border-l border-zinc-800 -mx-6 lg:mx-0 px-6 lg:px-0'>
            {roles.map((r, i) => (
              <button
                key={r.company}
                onClick={() => setActive(i)}
                className={`shrink-0 lg:shrink text-left px-4 py-3 font-mono text-sm whitespace-nowrap transition-all duration-200 border-b-2 lg:border-b-0 lg:border-l-2 -mb-px lg:mb-0 lg:-ml-px ${
                  active === i
                    ? 'border-accent text-accent bg-accent/5'
                    : 'border-transparent text-zinc-400 hover:text-accent hover:bg-zinc-800/30'
                }`}
              >
                {r.company}
              </button>
            ))}
          </div>

          {/* Active role detail */}
          <div className='animate-fade-up' key={active}>
            <h3 className='text-xl sm:text-2xl font-semibold text-white'>
              {role.title}{' '}
              <span className='text-accent'>@ {role.company}</span>
            </h3>
            {role.client && (
              <p className='text-zinc-500 text-sm mt-1'>{role.client}</p>
            )}
            <p className='font-mono text-zinc-500 text-sm mt-1 mb-6'>{role.dates}</p>

            <ul className='space-y-3'>
              {role.bullets.map((b, i) => (
                <li key={i} className='flex gap-3 text-zinc-300 leading-relaxed'>
                  <span className='text-accent shrink-0 mt-1.5'>▹</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className='flex flex-wrap gap-2 mt-6'>
              {role.tech.map((t) => (
                <span key={t} className='chip'>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
